/* SpawnAnything — campaign puzzle rules (pure, no DOM; used by the campaign UI and the tests).
   A mission from src/content/campaign.js becomes a TABS-style puzzle: the enemy army is fixed, the player buys an army
   (one unit type, any count), tool charges and extra buildings from a point budget and deploys them in the own half (x < 0).
   The enemy brings up to MAX_TROOPS troops (mission.puzzle.enemy = one fixed troop instead); reinforcement waves come
   from the enemy's last troop (the worker's trainer group owns the reserve), so they are converted into its unit type at
   equal points, and the troop the waves mostly consist of is put last.
   Stars: 1 for the win, 2 when less than stars[0] of the budget was spent, 3 below stars[1] (per mission, default 75 % / 50 %).
   Progress: localStorage key 'aw-campaign' { v, stars: { id: n }, best: { id: spent } }, every access in try/catch. */
import { CAMPAIGN, CAMPAIGN_MISSIONS } from '../content/campaign.js';
import { ERAS } from '../content/eras.js';
import { unitCost, toolCost } from './campaignCosts.js';
import { formationLayout } from '../sim/features/formation.js';
import { MAX_TROOPS } from '../game/groups.js';

export const STORE_KEY = 'aw-campaign';
export const DEFAULT_STARS = [0.75, 0.5];
// walls and other long buildings stand across the front (local x runs along the wall)
const ACROSS = new Set(['palisade', 'burgmauer', 'torhaus', 'sandsack']);

const missionIndex = id => CAMPAIGN_MISSIONS.findIndex(m => m.id === id);
export const missionById = id => CAMPAIGN_MISSIONS.find(m => m.id === id) || null;
export const chapterOf = m => CAMPAIGN.find(ch => ch.missions.includes(m)) || null;
export const nextMission = m => CAMPAIGN_MISSIONS[missionIndex(m.id) + 1] || null;

/** Enemy troops (enemies[], slot = index; enemy = the last one, which also brings the waves), converted waves, budget
    and star thresholds of a mission. */
export function puzzleOf(m) {
  const p = m.puzzle || {};
  const pts = list => (list || []).reduce((s, a) => s + unitCost(a.type, a.weapon) * a.count, 0);
  const wl = Array.isArray(p.waves) ? null : m.enemy.waves || [];
  const enemies = (p.enemy ? [p.enemy] : m.enemy.armies.slice(0, MAX_TROOPS)).map(e => ({ type: e.type, count: e.count, weapon: e.weapon, size: m.boss ? e.size || 1 : 1 })); // boss: its giant size
  if (wl && wl.length && enemies.length > 1) { // the troop the waves mostly bring trains them (else the largest troop): it goes last
    const by = {}; for (const w of wl) for (const a of w.armies) by[a.type] = (by[a.type] || 0) + unitCost(a.type, a.weapon) * a.count;
    const inW = enemies.filter(e => by[e.type]).sort((a, b) => by[b.type] - by[a.type]);
    const pick = inW[0] || enemies.slice().sort((a, b) => b.count - a.count)[0];
    enemies.push(...enemies.splice(enemies.indexOf(pick), 1));
  }
  const enemy = enemies[enemies.length - 1];
  const ec = unitCost(enemy.type, enemy.weapon) || 1;
  const waves = (wl || p.waves).map(w => ({ at: w.atSeconds, n: Math.max(1, Math.round(pts(w.armies) / ec)) }));
  const enemyPts = pts(enemies) + ec * waves.reduce((s, w) => s + w.n, 0);
  const budget = p.budget > 0 ? p.budget : Math.max(50, Math.round(enemyPts * 1.2 / 10) * 10);
  const stars = Array.isArray(p.stars) && p.stars.length === 2 ? p.stars.slice() : DEFAULT_STARS.slice();
  return { enemies, enemy, waves, budget, stars, enemyPts };
}

/** Number of stars for a result. */
export function starsFor(won, spent, budget, stars = DEFAULT_STARS) {
  if (!won) return 0;
  const f = spent / Math.max(1, budget);
  return f < stars[1] ? 3 : f < stars[0] ? 2 : 1;
}

/* ---------------- progress ---------------- */
export function emptyProgress() { return { v: 1, stars: {}, best: {}, bonus: {} }; }
export function loadProgress(storage = globalThis.localStorage) {
  try {
    const raw = storage && storage.getItem(STORE_KEY);
    const s = raw ? JSON.parse(raw) : null;
    if (!s || s.v !== 1 || typeof s.stars !== 'object' || !s.stars) return emptyProgress();
    const out = emptyProgress();
    for (const m of CAMPAIGN_MISSIONS) {
      const n = s.stars[m.id] | 0; if (n > 0) out.stars[m.id] = Math.min(3, n);
      const b = s.best && +s.best[m.id]; if (b > 0) out.best[m.id] = Math.round(b);
      const g = s.bonus && s.bonus[m.id]; if (Array.isArray(g)) { const ok = g.filter(k => BONUS_KINDS.includes(k)); if (ok.length) out.bonus[m.id] = [...new Set(ok)]; }
    }
    return out;
  } catch { return emptyProgress(); }
}
export function saveProgress(p, storage = globalThis.localStorage) {
  try { storage && storage.setItem(STORE_KEY, JSON.stringify(p)); return true; } catch { return false; }
}
/* Bonus goals (user decision 02.10.2026: fixed extra goals per mission): every mission asks that half of the own troop
   survives (pulls against the stars, which reward spending little); missions that sell tools also ask for a win without them. */
export const BONUS_KINDS = ['half', 'notools'];
const BONUS_TEXT = { half: 'Mindestens die Hälfte deiner Truppe überlebt', notools: 'Gewinne ohne Werkzeuge' };
/** Bonus goals of mission m with roster ro (rosterOf): [{ kind, text }]. */
export function bonusGoals(m, ro) {
  const out = [{ kind: 'half', text: BONUS_TEXT.half }];
  if (ro && ro.tools && ro.tools.length) out.push({ kind: 'notools', text: BONUS_TEXT.notools });
  return out;
}
/** Which bonus goals a won battle met: own = units placed, alive = own units alive at the end, toolsBought = charges. */
export function bonusMet(goals, { won, own, alive, toolsBought }) {
  if (!won) return [];
  return goals.filter(g => (g.kind === 'half' ? own > 0 && alive * 2 >= own : g.kind === 'notools' ? toolsBought === 0 : false)).map(g => g.kind);
}
/** Store met bonus goals; returns the kinds reached for the first time. */
export function recordBonus(p, m, kinds) {
  const had = p.bonus[m.id] || [], fresh = kinds.filter(k => !had.includes(k));
  if (fresh.length) p.bonus[m.id] = [...had, ...fresh];
  return fresh;
}

/** Store a result; returns { stars, improved, newUnlocks } (unlocks granted by a first win). */
export function recordResult(p, m, won, spent, budget, thresholds) {
  const stars = starsFor(won, spent, budget, thresholds), had = p.stars[m.id] | 0;
  if (stars > had) p.stars[m.id] = stars;
  if (won && (!p.best[m.id] || spent < p.best[m.id])) p.best[m.id] = spent;
  const u = m.unlocks || {};
  const newUnlocks = won && !had ? { units: u.units || [], buildings: u.buildings || [], tools: u.tools || [] } : null;
  return { stars, improved: stars > had, newUnlocks };
}
export const totalStars = p => Object.values(p.stars).reduce((s, n) => s + n, 0);
/** Linear campaign: a mission is open when it is the first one or its predecessor was won. */
export function isOpen(p, m) { const i = missionIndex(m.id); return i === 0 || (p.stars[CAMPAIGN_MISSIONS[i - 1].id] | 0) > 0; }
/** Everything unlocked by won missions before mission m (units, buildings, tools). */
export function unlockedBefore(p, m) {
  const out = { units: new Set(), buildings: new Set(), tools: new Set() };
  for (const q of CAMPAIGN_MISSIONS) {
    if (q === m) break;
    if (!((p.stars[q.id] | 0) > 0)) continue;
    for (const k of ['units', 'buildings', 'tools']) for (const x of (q.unlocks && q.unlocks[k]) || []) out[k].add(x);
  }
  return out;
}

/** What the player may buy in mission m: unit types (cheapest first), tools, extra buildings. Unlocks count within their
    own era (the chapter that unlocked them), so every chapter stays a puzzle of its own epoch. */
export function rosterOf(p, m) {
  const un = unlockedBefore(p, m), era = ERAS.find(e => e.key === m.era) || { units: [], buildings: [], tools: [] };
  const inEra = (set, list) => [...set].filter(k => list.includes(k));
  const units = [...new Set([...m.player.armies.map(a => a.type), ...((m.puzzle && m.puzzle.pool) || []), ...inEra(un.units, era.units)])]
    .filter(k => unitCost(k) > 0).sort((a, b) => unitCost(a) - unitCost(b) || a.localeCompare(b));
  const weapon = Object.fromEntries(m.player.armies.map(a => [a.type, a.weapon]));
  const tools = [...new Set([...(m.player.tools || []), ...inEra(un.tools, era.tools || [])])].filter(k => toolCost(k) > 0);
  const buildings = inEra(un.buildings, era.buildings);
  return { units, weapon, tools, buildings };
}

/* ---------------- battle setup ---------------- */
/** Mission buildings as sandbox placements: the player's first, then the enemy's (ids in the sim follow this order). */
export function missionPlacements(m) {
  const pl = (b, team) => ({ key: b.key, team, level: b.level, x: b.at[0], z: b.at[1], rot: ACROSS.has(b.key) ? 1 : 0 });
  return [...(m.player.buildings || []).map(b => pl(b, 0)), ...(m.enemy.buildings || []).map(b => pl(b, 1))];
}

/** The `campaign` field of the worker's setup message (see src/sim/features/campaign.js). */
export function simConfig(m, pz = puzzleOf(m)) {
  const nP = (m.player.buildings || []).length;
  const idsOf = (list, key, off) => (list || []).map((b, i) => (b.key === key ? off + i : -1)).filter(i => i >= 0);
  const objectives = m.objectives.map(o => {
    if (o.kind === 'destroy_building') return { kind: o.kind, ids: idsOf(m.enemy.buildings, o.building, nP) };
    if (o.kind === 'protect_building') return { kind: o.kind, ids: idsOf(m.player.buildings, o.building, 0), seconds: o.seconds || 0 };
    if (o.kind === 'capture') return { kind: o.kind, at: o.at.slice(), radius: o.radius, seconds: o.seconds };
    if (o.kind === 'survive') return { kind: o.kind, seconds: o.seconds };
    return { kind: o.kind };
  });
  const R = m.map.terrain.battleRadius;
  const timed = Math.max(0, ...m.objectives.map(o => o.seconds || 0));
  return { objectives, waves: pz.waves.map(w => ({ ...w })), waveX: Math.min(110, Math.max(45, R * 0.8)), limit: timed ? timed + 180 : 600 };
}

/** Distance between the two fronts, as the sim's count-mode layout computes it from the weapon ranges. */
export const frontGap = (...ranges) => Math.min(160, Math.max(44, Math.max(...ranges) * 1.25 + 26));

/** Seeded block formation (the sim's count-mode block, no jitter) of n units with radius r for team t, as flat
    [x, z, …] for the paint mode: only on its own side (|x| >= 1.5), walkable cells and inside the arena.
    lim = largest |z| of a row (the battle radius), walkable(x, z) = no river. */
export function blockPositions(t, r, n, gap, lim, walkable = () => true) {
  const out = [];
  if (!(n > 0)) return out;
  for (let extra = 0; extra < 4 && out.length < n * 2; extra++) { // river in the way: lay out more and skip the wet ones
    const m = Math.ceil(n * (1 + extra * 0.5)) + 4;
    const X = new Float32Array(m), Z = new Float32Array(m), ROT = new Float32Array(m);
    let seed = 4242 + t; const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
    formationLayout({ ext: [0, 0] }, 0, { count: m, r, start: 0 }, t, gap, rnd, X, Z, ROT, Math.max(30, lim));
    out.length = 0;
    for (let k = 0; k < m && out.length < n * 2; k++) {
      const x = X[k], z = Z[k];
      if (Math.abs(x) > 215 || Math.abs(z) > 215 || (t === 0 ? x > -1.5 : x < 1.5) || !walkable(x, z)) continue;
      out.push(x, z);
    }
  }
  return out;
}

/** Start positions of the enemy troops: one block each, close combat in front, longer ranges further back (always
    behind the blocks before them). rOf(type) = unit radius, rangeOf(type, weapon) = 0 for melee. Returns flat
    pos [x, z, …] and the troop slot of every position. */
export function enemyLayout(pz, rOf, rangeOf, lim, walkable = () => true) {
  const order = pz.enemies.map((e, slot) => ({ e, slot, rg: rangeOf(e.type, e.weapon) })).sort((a, b) => a.rg - b.rg || a.slot - b.slot);
  const pos = [], slots = [];
  let back = 0;
  for (const o of order) {
    const p = blockPositions(1, rOf(o.e.type), o.e.count, Math.max(frontGap(o.rg), back), lim, walkable);
    let maxX = 0;
    for (let k = 0; k < p.length; k += 2) { pos.push(p[k], p[k + 1]); slots.push(o.slot); maxX = Math.max(maxX, p[k] + rOf(o.e.type)); }
    if (p.length) back = Math.max(back, 2 * (maxX + 4));
  }
  return { pos, slots };
}

/** Total units the enemy needs slots for beyond the starting army (wave reinforcements). */
export const waveUnits = pz => pz.waves.reduce((s, w) => s + w.n, 0);

/* ---------------- texts ---------------- */
const secs = s => (s >= 60 && s % 60 === 0 ? `${s / 60} ${s === 60 ? 'Minute' : 'Minuten'}` : `${s} Sekunden`);
/** German one-liner per objective; names(key) gives a building's display name, m (optional) the mission: with several
    buildings of that key the text says what the sim checks (destroy: all of them, protect: one has to stay). */
export function objectiveText(o, names = k => k, m = null) {
  const n = side => (m && (m[side].buildings || []).filter(b => b.key === o.building).length) || 1;
  switch (o.kind) {
    case 'destroy_all': return (m && puzzleOf(m).waves.length) ? 'Besiege alle Gegner, auch die Verstärkung' : 'Besiege alle Gegner';
    case 'survive': return `Überlebe ${secs(o.seconds)}`;
    case 'destroy_building': return n('enemy') > 1 ? `Zerstöre: ${names(o.building)} ×${n('enemy')} (alle)` : `Zerstöre: ${names(o.building)}`;
    case 'protect_building': {
      const k = n('player'), b = names(o.building);
      if (k > 1) return o.seconds ? `Beschütze ${secs(o.seconds)} lang: ${b} ×${k} (eins muss stehen bleiben)` : `${b} ×${k}: eins muss stehen bleiben`;
      return o.seconds ? `Beschütze ${b} ${secs(o.seconds)} lang` : `${b} darf nicht fallen`;
    }
    case 'capture': return `Halte die Zone ${secs(o.seconds)} lang`;
    default: return o.kind;
  }
}
